import { styled, keyframes } from '@mui/material/styles';
import { alpha, Box, Card, CardProps as MuiCardProps, Tooltip } from '@mui/material';
import { FaArrowsRotate } from 'react-icons/fa6';
import { washedOutColor } from 'utils/helpers.tsx';
import { TestDataType } from 'api/model.ts';
import { Dataset, Description, LibraryAdd, QuestionMark, Restore, SvgIconComponent } from '@mui/icons-material';
import { NodeIssueTooltip } from './NodeIssueTooltip';

interface CardProps extends MuiCardProps {
  color?: string;
  selected?: boolean;
  error?: boolean;
  highlightColor?: string;
  progress?: number;
  disabled?: boolean;
  statusColor?: string;
  statusPulse?: boolean;
}

const errorPulsate = keyframes`
  0% {
    box-shadow: 0 0 0 0px rgba(255, 0, 0, 0.9);
  }
  50% {
    box-shadow: 0 0 15px 10px rgba(255, 0, 0, 0.7);
  }
  100% {
    box-shadow: 0 0 0 0px rgba(255, 0, 0, 0.9);
  }
`;

const scalePulsate = keyframes`
  0% {
    transform: scale(1);
  }
  50% {
    transform: scale(1.3);
  }
  100% {
    transform: scale(1);
  }
`;

const rotationAnimation = keyframes`
  0% {
    transform: rotate(0deg);
  }
  100% {
    transform: rotate(360deg);
  }
`;

export const NodeBadge = styled('div')({
  position: 'absolute',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: 2,
  minHeight: 20,
  height: 20,
  padding: '0 3px',
  borderRadius: 100,
  fontSize: '0.9rem',
  fontWeight: 700,
  lineHeight: 1,
  color: '#E4EDFF',
  backgroundColor: 'rgba(7, 15, 30, 0.62)',
  backdropFilter: 'blur(2px)',
  boxShadow: '0 1px 4px rgba(0, 0, 0, 0.35)',
});

const TEST_DATA_ICONS: Record<TestDataType, SvgIconComponent> = {
  [TestDataType.FILE]: Description,
  [TestDataType.DATAFRAME]: Dataset,
  [TestDataType.NEW_COLUMN]: LibraryAdd,
  [TestDataType.LATEST_RESULT]: Restore,
};

type TestDataIconProps = {
  type: TestDataType;
  validationError?: string;
};

export const TestDataIcon = ({ type, validationError }: TestDataIconProps) => {
  const Icon = TEST_DATA_ICONS[type] ?? QuestionMark;

  return (
    <NodeBadge style={{ bottom: 4, right: 4, width: 20 }}>
      {validationError ? (
        <Tooltip title={<NodeIssueTooltip severity="error" title="Validation error" messages={[validationError]} />}>
          <Icon
            sx={(theme) => ({
              fontSize: 15,
              color: theme.palette.error.light,
              animation: `${scalePulsate} 2s infinite ease-in-out`,
            })}
          />
        </Tooltip>
      ) : (
        <Icon sx={{ fontSize: 15 }} />
      )}
    </NodeBadge>
  );
};

export const FaArrowsRotateAnimated = styled(FaArrowsRotate)(() => ({
  animation: `${rotationAnimation} 1.5s infinite linear`,
  display: 'inline-block',
}));

export const NodeDetailsTooltipBox = styled(Box)(({ theme }) => ({
  overflowY: 'auto',
  overflowX: 'hidden',
  maxHeight: theme.spacing(50),
  padding: theme.spacing(3),
}));

export const NodeCard = styled(Card, {
  shouldForwardProp: (prop) =>
    prop !== 'color' &&
    prop !== 'selected' &&
    prop !== 'progress' &&
    prop !== 'disabled' &&
    prop !== 'statusColor' &&
    prop !== 'statusPulse',
})<CardProps>(({ theme, color, selected, progress, error, highlightColor, disabled, statusColor, statusPulse }) => {
  // A running node's glow gently pulses so in-progress work draws the eye.
  const statusPulseAnimation =
    statusColor &&
    keyframes`
      0%, 100% { box-shadow: 0 0 10px 1px ${alpha(statusColor, 0.35)}; }
      50% { box-shadow: 0 0 24px 6px ${alpha(statusColor, 0.7)}; }
    `;

  return {
    width: theme.spacing(10),
    height: theme.spacing(10),
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    fontSize: theme.spacing(3.5),
    borderRadius: '22.5%',
    backgroundColor: color,
    background: color,
    ...(disabled && {
      opacity: 0.5,
      cursor: 'not-allowed',
      boxShadow: 'none',
      pointerEvents: 'none',
    }),
    ...(progress !== undefined && {
      background: `linear-gradient(
    to right,
    ${color} ${progress}%,
    ${washedOutColor(color ?? '')} ${progress}%
  )`,
    }),
    color: color ? theme.palette.getContrastText(color) : theme.palette.common.white,
    boxShadow: statusColor ? `0 0 18px 2px ${alpha(statusColor, 0.55)}` : selected ? theme.shadows[4] : 'none',
    animation: error
      ? `${errorPulsate} 1.5s infinite ease-in-out`
      : statusPulse && statusPulseAnimation
        ? `${statusPulseAnimation} 1.6s infinite ease-in-out`
        : 'none',
    transition: theme.transitions.create(['box-shadow', 'filter']),

    border: highlightColor ? `4px solid ${highlightColor}` : 'none',

    '&:hover': {
      boxShadow: statusColor
        ? `0 0 20px 4px ${alpha(statusColor, 0.6)}`
        : `0 0 0 2px ${alpha(theme.palette.primary.main, 0.35)}, 0 8px 20px rgba(0, 0, 0, 0.35)`,
      filter: 'brightness(1.06)',
      cursor: 'pointer',
    },
  };
});
